import React from 'react';
import { CopyIcon, FlagIcon, GripVerticalIcon, PencilIcon, TimerIcon, Trash2Icon } from 'lucide-react';
import { estiloColor } from '../../data/flujos';
import type { EtapaFlujo } from '../../types';
export function TarjetaEtapa({
  etapa,
  indice,
  arrastrando,
  destino,
  onEditar,
  onDuplicar,
  onEliminar,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd
}: {
  etapa: EtapaFlujo;
  indice: number;
  arrastrando: boolean;
  destino: boolean;
  onEditar: () => void;
  onDuplicar: () => void;
  onEliminar: () => void;
  onDragStart: () => void;
  onDragOver: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
}) {
  const color = estiloColor(etapa.color);
  return <div draggable onDragStart={onDragStart} onDragOver={e => {
    e.preventDefault();
    onDragOver();
  }} onDrop={e => {
    e.preventDefault();
    onDrop();
  }} onDragEnd={onDragEnd} className={`group rounded-lg border bg-white px-4 py-3.5 transition-colors duration-150 ${arrastrando ? 'border-marino-400 opacity-60' : destino ? 'border-marino-600 bg-marino-50' : 'border-gris-200 hover:border-marino-300'}`} data-id="element-709" data-mp="15c2s2lqd2etx">
      <div className="flex items-start gap-3" data-id="element-710" data-mp="3f2csh2styqv8">
        <span className="mt-0.5 cursor-grab text-gris-300 transition-colors duration-150 group-hover:text-gris-500 active:cursor-grabbing" title="Arrastra para reordenar" aria-hidden="true" data-id="element-711" data-mp="3k04l5z1auh0h">
          <GripVerticalIcon className="h-4 w-4" data-id="element-712" data-mp="fm64sld5xcvm" />
        </span>

        <div className="min-w-0 flex-1" data-id="element-713" data-mp="1uj66evqzs9o">
          <div className="flex flex-wrap items-center gap-2" data-id="element-714" data-mp="1tghmdlwe1wx">
            <h3 className="text-sm font-semibold text-marino-900" data-id="element-715" data-mp="6tmbm0fn40i6">{etapa.nombre}</h3>
            {etapa.etiqueta && <span className={`inline-flex items-center rounded px-2 py-0.5 text-2xs font-semibold uppercase tracking-wide ${color.chip}`} data-id="element-716" data-mp="uingxet6evj8">
                {etapa.etiqueta}
              </span>}
            {etapa.esHito && <span className="inline-flex items-center gap-1 rounded bg-ambar-50 px-2 py-0.5 text-2xs font-bold uppercase tracking-wide text-ambar-700 ring-1 ring-ambar-100" data-id="element-717" data-mp="146t0pxzfgt5p">
                <FlagIcon className="h-3 w-3" aria-hidden="true" data-id="element-718" data-mp="z5vaqz86eutn" />
                Hito · alerta automática
              </span>}
          </div>

          {etapa.descripcion && <p className="mt-1 text-xs text-gris-600" data-id="element-719" data-mp="q8chx8oajc77">{etapa.descripcion}</p>}

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-2xs text-gris-500" data-id="element-720" data-mp="leqgkcnkuuqp">
            <span className="inline-flex items-center gap-1.5" data-id="element-721" data-mp="ghq6zrrir9uu">
              <TimerIcon className="h-3.5 w-3.5 text-gris-400" aria-hidden="true" data-id="element-722" data-mp="biwthzc8fiuo" />
              {etapa.tienePlazo ? <span className="tabular" data-id="element-723" data-mp="6hr162mhhxlo">
                  Plazo de <span className="font-semibold text-gris-700" data-id="element-724" data-mp="2hfk0ztbyfey">{etapa.diasSugeridos}</span>{' '}
                  días sugeridos
                </span> : 'Sin plazo asociado'}
            </span>
            <span className="tabular" data-id="element-725" data-mp="2i52wb575pvo5">Orden {indice + 1}</span>
          </div>

          {etapa.documentosEsperados.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-726" data-mp="2n3e3f862hs2o">
              {etapa.documentosEsperados.map(d => <span key={d} className="rounded bg-gris-100 px-1.5 py-0.5 text-2xs text-gris-600" data-id="element-727" data-mp="1ugtfyispq04e">
                  {d}
                </span>)}
            </div>}
        </div>

        <div className="flex shrink-0 items-center gap-1" data-id="element-728" data-mp="24e7lvoh0f6ig">
          <button type="button" onClick={onEditar} aria-label={`Editar etapa ${etapa.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-729" data-mp="2967pps07ajkb">
            <PencilIcon className="h-4 w-4" data-id="element-730" data-mp="3ifdp5bqe2c2j" />
          </button>
          <button type="button" onClick={onDuplicar} aria-label={`Duplicar etapa ${etapa.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-gris-200 hover:text-marino-700" data-id="element-731" data-mp="3ed8v4bw13dfd">
            <CopyIcon className="h-4 w-4" data-id="element-732" data-mp="5vul8ar4cp21" />
          </button>
          <button type="button" onClick={onEliminar} aria-label={`Eliminar etapa ${etapa.nombre}`} className="flex h-8 w-8 items-center justify-center rounded border border-transparent text-gris-400 transition-colors duration-150 hover:border-peligro-100 hover:text-peligro-600" data-id="element-733" data-mp="yck36jcfe4l">
            <Trash2Icon className="h-4 w-4" data-id="element-734" data-mp="2gq09mgw124dd" />
          </button>
        </div>
      </div>
    </div>;
}